// Event Invite Modal Web Component
import { escapeHtml } from '../utils/html-utils.js';

class EventInviteModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isLoading = false;
    this.eventId = null;
    this.activeTab = 'friends'; // friends, external, or lists
    this.friends = [];
    this.inviteLists = [];
    this.selectedFriends = [];
    this.externalEmails = [];
    this.loadingFriends = false;
    this.loadingInviteLists = false;
    this.invitedUserIds = new Set(); // Track which friends are already invited
    this.currentInvitations = [];
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  open(eventId) {
    this.eventId = eventId;
    this.isOpen = true;
    this.activeTab = 'friends';
    this.selectedFriends = [];
    this.externalEmails = [];
    this.invitedUserIds.clear();
    this.currentInvitations = [];
    this.render();
    this.loadInvitations();
    this.loadFriends();
    this.loadInviteLists();
  }

  async loadInvitations() {
    try {
      const response = await window.API.request(`/events/${this.eventId}/invitations`);
      const invitations = response.invitations || [];
      this.currentInvitations = invitations;
      // Build set of invited user IDs
      this.invitedUserIds.clear();
      invitations.forEach(inv => {
        if (inv.user_id) {
          this.invitedUserIds.add(inv.user_id);
        }
      });
      this.updateTabContent();
    } catch (error) {
      // 403/404 are OK - means user can't see invites or there are none
      if (error.statusCode !== 403 && error.statusCode !== 404) {
        window.logger.error('Failed to load invitations:', error);
      }
    }
  }

  close() {
    this.isOpen = false;
    this.render();
  }

  async loadFriends() {
    if (this.loadingFriends) return;

    this.loadingFriends = true;
    const tabContent = this.querySelector('.tab-content');
    if (tabContent && this.activeTab === 'friends') {
      tabContent.innerHTML = `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading friends...</p>
        </div>
      `;
    }

    try {
      const response = await window.API.request('/friends');
      this.friends = response.friends || [];
    } catch (error) {
      window.logger.error('Failed to load friends:', error);
      this.friends = [];
    } finally {
      this.loadingFriends = false;
      this.updateTabContent();
    }
  }

  async loadInviteLists() {
    if (this.loadingInviteLists) return;

    this.loadingInviteLists = true;
    const tabContent = this.querySelector('.tab-content');
    if (tabContent && this.activeTab === 'lists') {
      tabContent.innerHTML = `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading invite lists...</p>
        </div>
      `;
    }

    try {
      const response = await window.API.request('/invite-lists');
      this.inviteLists = response.invite_lists || [];
    } catch (error) {
      window.logger.error('Failed to load invite lists:', error);
      this.inviteLists = [];
    } finally {
      this.loadingInviteLists = false;
      this.updateTabContent();
    }
  }

  updateTabContent() {
    const tabContent = this.querySelector('.tab-content');
    if (tabContent) {
      // Show only the active tab's content
      tabContent.innerHTML = this.renderActiveTab();
      this.attachTabListeners();
    }
  }

  renderActiveTab() {
    if (this.activeTab === 'friends') return this.renderFriendsTab();
    if (this.activeTab === 'external') return this.renderExternalTab();
    if (this.activeTab === 'lists') return this.renderListsTab();
    return '';
  }

  attachTabListeners() {
    // Re-attach listeners for the current tab content
    if (this.activeTab === 'friends') {
      const friendCheckboxes = this.querySelectorAll('.friend-checkbox');
      friendCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.selectedFriends = Array.from(this.querySelectorAll('.friend-checkbox:checked'))
            .map(cb => parseInt(cb.value));
        });
      });
    } else if (this.activeTab === 'external') {
      const emailList = this.querySelector('#emailList');
      if (emailList) {
        emailList.addEventListener('input', (e) => {
          this.externalEmails = e.target.value.split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0);
        });
      }
    }
  }

  render() {
    this.innerHTML = `
      <div class="event-invite-modal ${this.isOpen ? 'open' : ''}" id="inviteModal">
        <div class="modal-backdrop" id="modalBackdrop"></div>
        <div class="modal-container">
          <div class="modal-header">
            <h3>📨 Invite People to Event</h3>
            <button class="close-btn" id="closeBtn">×</button>
          </div>
          
          <div class="modal-body">
            <div class="tabs" id="tabs">
              <button class="tab ${this.activeTab === 'friends' ? 'active' : ''}" data-tab="friends">
                👥 Friends
              </button>
              <button class="tab ${this.activeTab === 'external' ? 'active' : ''}" data-tab="external">
                📧 Email
              </button>
              <button class="tab ${this.activeTab === 'lists' ? 'active' : ''}" data-tab="lists">
                📋 Invite Lists
              </button>
            </div>

            <div class="tab-content">
              ${this.renderActiveTab()}
            </div>

            <div class="form-actions">
              <button type="button" class="btn btn-secondary" id="cancelBtn">Cancel</button>
              <button type="button" class="btn btn-primary" id="sendBtn">
                <span class="btn-text">Send Invitations</span>
                <span class="btn-loading" style="display: none;">⏳ Sending...</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  renderFriendsTab() {
    if (this.activeTab !== 'friends') return '';

    if (this.loadingFriends) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading friends...</p>
        </div>
      `;
    }

    if (this.friends.length === 0) {
      return `
        <div class="empty-state">
          <p>No friends to invite yet.</p>
        </div>
      `;
    }

    return `
      <div class="friends-list">
        ${this.friends.map(friend => {
    const isAlreadyInvited = this.invitedUserIds.has(friend.id);
    return `
            <div class="friend-item ${isAlreadyInvited ? 'disabled' : ''}" data-friend-id="${friend.id}">
              <label class="checkbox-label">
                <input type="checkbox" class="friend-checkbox" value="${friend.id}" 
                  ${this.selectedFriends.includes(friend.id) ? 'checked' : ''}
                  ${isAlreadyInvited ? 'disabled' : ''}>
                <span>${friend.username}${isAlreadyInvited ? ' ✓ (already invited)' : ''}</span>
              </label>
            </div>
          `;
  }).join('')}
      </div>
    `;
  }

  renderExternalTab() {
    if (this.activeTab !== 'external') return '';
    const contactInvitations = this.currentInvitations
      .filter(inv => !inv.user_id && (inv.email));

    return `
      <div class="external-invites">
        <div class="form-group">
          <label>Email Addresses (one per line):</label>
          <textarea id="emailList" rows="4" placeholder="friend1@example.com&#10;friend2@example.com">${this.externalEmails.join('\n')}</textarea>
          <small class="input-hint">Enter email addresses, one per line</small>
        </div>

        <div class="existing-invites">
          <div class="existing-invites-header">
            <span>Already invited</span>
            <span class="badge">${contactInvitations.length}</span>
          </div>
          ${contactInvitations.length === 0
    ? '<p class="empty-state small">No email invitations have been sent yet.</p>'
    : `
            <div class="invited-chip-list">
              ${contactInvitations.map(inv => `
                <div class="invite-chip">
                  <div class="invite-chip-label">
                    ${escapeHtml(inv.email || '')}
                  </div>
                  <span class="invite-status status-${inv.rsvp_status || 'pending'}">
                    ${(inv.rsvp_status || 'pending').toUpperCase()}
                  </span>
                </div>
              `).join('')}
            </div>
          `}
        </div>
      </div>
    `;
  }

  renderListsTab() {
    if (this.activeTab !== 'lists') return '';

    if (this.loadingInviteLists) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading invite lists...</p>
        </div>
      `;
    }

    if (this.inviteLists.length === 0) {
      return `
        <div class="empty-state">
          <p>No invite lists available. Create one first!</p>
        </div>
      `;
    }

    return `
      <div class="invite-lists">
        ${this.inviteLists.map(list => `
          <div class="list-item" data-list-id="${list.id}">
            <label class="checkbox-label">
              <input type="radio" name="inviteList" class="list-radio" value="${list.id}">
              <span>
                <strong>${list.name}</strong>
                <small>${list.member_count || 0} members</small>
              </span>
            </label>
          </div>
        `).join('')}
      </div>
    `;
  }

  attachEventListeners() {
    const backdrop = this.querySelector('#modalBackdrop');
    backdrop?.addEventListener('click', () => this.close());

    const closeBtn = this.querySelector('#closeBtn');
    closeBtn?.addEventListener('click', () => this.close());

    const cancelBtn = this.querySelector('#cancelBtn');
    cancelBtn?.addEventListener('click', () => this.close());

    const sendBtn = this.querySelector('#sendBtn');
    sendBtn?.addEventListener('click', () => this.sendInvitations());

    // Tab switching
    const tabs = this.querySelectorAll('.tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        this.activeTab = e.target.dataset.tab;
        this.updateTabContent();
        // Update tab active states
        tabs.forEach(t => t.classList.remove('active'));
        e.target.classList.add('active');
      });
    });

    // Attach listeners for current tab content
    this.attachTabListeners();
  }

  async sendInvitations() {
    if (!this.eventId) return;

    const sendBtn = this.querySelector('#sendBtn');
    sendBtn.classList.add('loading');
    sendBtn.disabled = true;

    try {
      const requestBody = {};

      if (this.activeTab === 'friends') {
        requestBody.user_ids = this.selectedFriends;
      } else if (this.activeTab === 'external') {
        const emailText = this.querySelector('#emailList').value;

        requestBody.emails = emailText.split('\n')
          .map(e => e.trim())
          .filter(e => e.length > 0);
      } else if (this.activeTab === 'lists') {
        const selectedList = this.querySelector('.list-radio:checked');
        if (selectedList) {
          requestBody.invite_list_id = parseInt(selectedList.value);
        }
      }

      await window.API.request(`/events/${this.eventId}/invite`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      this.close();

      // Show success notification
      document.dispatchEvent(new CustomEvent('event-invites-sent', {
        detail: { eventId: this.eventId }
      }));

      // Show toast after modal closes
      setTimeout(() => {
        const toast = document.createElement('div');
        toast.className = 'toast-notification';
        toast.textContent = 'Invitations sent successfully!';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 3000);
      }, 350);

    } catch (error) {
      alert(error.message || 'Failed to send invitations');
    } finally {
      sendBtn.classList.remove('loading');
      sendBtn.disabled = false;
    }
  }

}

customElements.define('event-invite-modal', EventInviteModal);

